import { ReactElement, useCallback, useState } from 'react';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { useContainingAppVersion } from '../../util/billing.ts';
import { containingAppPlatformLabel } from '../../util/containing_app.ts';
import { ForwardArrow } from '../../util/forward_arrow.tsx';
import { formatOperatingSystem, useOperatingSystem } from '../../util/operating_system.ts';
import { Header } from '../header.tsx';
import '../settings_page.css';
import { CacheStoragePage } from './cache_storage_page.tsx';
import { DialogPage, SubViewOverlay } from './dialog_page.tsx';
import { LocalStoragePage } from './local_storage_page.tsx';
import { LoggingPage } from './logging_page.tsx';

enum AboutSubView {
  LocalStorage = 'Local Storage',
  CacheStorage = 'Cache Storage',
  Logging = 'Logging',
}

export const AboutPage = () => {
  const [subView, setSubView] = useState<AboutSubView | undefined>();
  const containingAppVersion = useContainingAppVersion();
  const operatingSystem = useOperatingSystem();
  const [bundleHash] = useLocalStorage(LocalStorageKey.BundleHash);

  const closeSubView = useCallback(() => {
    setSubView(undefined);
  }, []);

  function renderSubView(): ReactElement | undefined {
    switch (subView) {
      case AboutSubView.LocalStorage:
        return <LocalStoragePage />;
      case AboutSubView.CacheStorage:
        return <CacheStoragePage />;
      case AboutSubView.Logging:
        return <LoggingPage />;
      default:
        return undefined;
    }
  }

  return (
    <>
      <DialogPage title="About">
        <div className="settingsContent">
          <div className="settingsBox">
            <table className="settings">
              <tbody>
                {containingAppVersion && (
                  <tr className="setting arrow">
                    <td className="settingName">
                      {containingAppPlatformLabel(containingAppVersion.platform)} App Version
                    </td>
                    <td className="currentValue">{containingAppVersion.version}</td>
                    <td className="settingAction"></td>
                  </tr>
                )}
                <tr className="setting arrow">
                  <td className="settingName">Operating System</td>
                  <td className="currentValue">{formatOperatingSystem(operatingSystem)}</td>
                  <td className="settingAction"></td>
                </tr>
                <tr className="setting arrow">
                  <td className="settingName">Bundle Hash</td>
                  <td className="currentValue">{bundleHash ?? 'localhost'}</td>
                  <td className="settingAction"></td>
                </tr>
                <tr className="setting arrow" onClick={() => setSubView(AboutSubView.LocalStorage)}>
                  <td className="settingName">Local Storage</td>
                  <td className="currentValue" />
                  <td className="settingAction">
                    <ForwardArrow />
                  </td>
                </tr>
                <tr className="setting arrow" onClick={() => setSubView(AboutSubView.CacheStorage)}>
                  <td className="settingName">Cache Storage</td>
                  <td className="currentValue" />
                  <td className="settingAction">
                    <ForwardArrow />
                  </td>
                </tr>
                <tr className="setting arrow" onClick={() => setSubView(AboutSubView.Logging)}>
                  <td className="settingName">Logging</td>
                  <td className="currentValue" />
                  <td className="settingAction">
                    <ForwardArrow />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DialogPage>
      {subView && (
        <SubViewOverlay onClose={closeSubView}>
          {(close) => (
            <>
              <Header title={subView} onBack={close} />
              {renderSubView()}
            </>
          )}
        </SubViewOverlay>
      )}
    </>
  );
};
